<template>
    <b-container fluid v-if="ratesData" class="rates-page">
        <div v-if="wizardData" class="mb-3">
            <div class="d-flex justify-content-between align-items-center pt-3">
                <div>
                    <b
                        >{{ wizardData.name }} -
                        {{ selectedTab ? selectedTab.headerTitle : '' }}
                        <small v-if="isRatesTab && ratesData.active_template" class="text-danger">
                            <b>
                                (The "{{ ratesData.active_template.name }}" template is assigned to this property. Rates
                                must be adjusted from the
                                <a
                                    target="_blank"
                                    :href="`/app/seasonality-template/templates/${ratesData.active_template.id}`"
                                    >template</a
                                >)</b
                            ></small
                        ></b
                    >
                </div>
            </div>
            <hr />
        </div>
        <div class="settings-tab-wrapper d-flex flex-lg-row flex-column">
            <b-button
                v-for="tab in ratesTabs"
                :key="tab.id"
                :active="getIsActive(tab.id)"
                exact-active-class="active-nav-button"
                variant="default"
                @click="currentTab = tab.id"
                href="#"
                >{{ tab.tabName }}</b-button
            >
            <b-button
                v-if="isRatesTab && !ratesData.read_only"
                variant="default"
                class="mb-lg-0 mb-1 mr-md-1 md-0"
                @click="$bvModal.show(copyRatesModalId)"
            >
                <font-awesome-icon class="mr-1" :icon="['fas', 'copy']" />
                Copy Rates</b-button
            >
            <b-button
                v-if="isRatesTab && !ratesData.read_only"
                variant="default"
                class="mb-lg-0 mb-1"
                @click="$bvModal.show(clonePreviousYearRatesModalId)"
            >
                <font-awesome-icon class="mr-1" :icon="['fas', 'clone']" />
                Clone Previous Year</b-button
            >
        </div>
        <div class="mt-2" v-if="isRatesTab && ratesList">
            <grid
                class="rates-list-grid mb-2"
                :rowData="ratesList.rates"
                :columnDefs="columnDefs"
                :isSearch="false"
                :isAutoWidth="true"
                :rowBuffer="500"
            />
            <year-calendar
                v-if="renderCalendar"
                :rates="ratesList.rates"
                :rates-settings="ratesData.rate_settings"
                :weekend-days="ratesData.rate_settings.js_weekend_days"
                :current-year="ratesData.current_year"
                :read-only="ratesData.read_only"
                @year-changed="(value) => onChangeYear(value)"
                @day-clicked="onDayClick"
                @select-range="onSelectRange"
                :data-source="dataSource"
            />
            <create-edit-rate-modal
                :title="rateModalTitle"
                :rateId="values ? values.id : 0"
                :currentYear="ratesData.current_year"
                :initValues="values"
                :maxGuests="ratesData.max_guests"
                :modalId="'create-rate-modal'"
                :objects="ratesList.rates"
                :rateSettings="ratesData.rate_settings"
                :ruleSettings="ratesData.rule_settings"
                :isDefault="values ? values.default : false"
                :loading="loading"
                :editMode="editMode"
                :newObject="newRateObject"
                @submit="createEditRate"
                @reset="onReset"
                @delete="onRemoveRateFromModal"
            />
            <modal
                :id="copyRatesModalId"
                title="Copy Rates"
                :ok-disabled="!selectedSource || !currentSelectedRows.length"
                ok-title="Copy"
                @ok.prevent="copyRates"
                size="lg"
                @hide="clearCopyRates"
            >
                <busy-modal :show="loading" />
                <form-select
                    v-if="initialData"
                    floatingLabel="From Property"
                    v-model="selectedSource"
                    :options="propertyChoices"
                    @change="fetchRatesForCopy"
                />
                <div v-show="ratesForCopy.length > 0">
                    <grid
                        ref="grid_copy_rates"
                        gridTheme="ag-theme-alpine-property-setup"
                        row-selection="multiple"
                        :suppress-row-click-selection="true"
                        :column-defs="ratesCopyColumnDefs"
                        :header-height="50"
                        :rowData="ratesForCopy"
                        :isAutoWidth="true"
                        @selection-changed="handleSelectionChange"
                        @on-row-data-updated="onRowUpdated"
                        :isSearch="false"
                        :isAutoHeight="true"
                    />
                </div>
                <small class="text-danger">
                    All conflicting rates will be dumped and selected rates will be applied. Do you want to proceed?
                </small>
            </modal>
            <modal :id="clonePreviousYearRatesModalId" title="Clone Previous Year Rates" @ok.prevent="cloneRates">
                Are you sure want to clone previous year rates? All of
                {{ ratesData.current_year }}
                rates will be deleted!
                <busy-modal :show="loading" />
            </modal>
        </div>
        <div class="mt-2" v-if="isRatesSettingsTab && ratesData">
            <rates-settings :ratesData="ratesData.rate_settings" @reload-all-data="fetchAllData" />
        </div>
        <div class="mt-2" v-if="isRulesSettingsTab && ratesData">
            <rules-settings :rulesData="ratesData.rule_settings" @reload-all-data="fetchAllData" />
        </div>
        <modal id="remove-rate-modal" title="Warning" @ok="removeRate(removeRateId)">
            <busy-modal :show="loading" />
            Are you sure you want to delete this rate?
        </modal>
    </b-container>
</template>

<script>
    import { mapGetters } from 'vuex';
    import api from '../../../../api';
    import clone from 'lodash/clone';
    import { RENTAL_PROPERTY_SETUP_RATES_ENDPOINT } from '../../../../endpoints';
    import { formatResponseError } from '../../../../utils/api';
    import ActionCellRenderer from './rates-action-cell-renderer.vue';
    import Grid from '../../../generic/grid/grid.vue';
    import RatesSettings from './rates-settings.vue';
    import RulesSettings from './rules-settings.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import FormSelect from '../../../generic/form-select/form-select.vue';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import CreateEditRateModal from '../../../common/create-edit-rate-season-modal.vue';
    import { ratesMixin } from './rates-mixin';
    import format from 'date-fns/format';
    import { getRate } from '../utils';
    import { newRateObject } from '../constants';
    import { defineAsyncComponent } from 'vue';

    const YearCalendar = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-year-calendar" */
            '../../../generic/year-calendar/year-calendar.vue'
        )
    );

    export default {
        name: 'Rates',
        components: {
            Grid,
            YearCalendar,
            RatesSettings,
            Modal,
            FormSelect,
            BusyModal,
            RulesSettings,
            CreateEditRateModal,
            actionCellRenderer: ActionCellRenderer,
        },
        mixins: [ratesMixin],
        data() {
            return {
                newRateObject,
                loading: false,
                ratesData: null,
                ratesList: null,
                currentTab: 'rates_rules',
                copyRatesModalId: 'copy-rates-modal-id',
                clonePreviousYearRatesModalId: 'clone-rates-modal-id',
                selectedSource: null,
                ratesForCopy: [],
                currentYear: null,
                currentSelectedRows: [],
                values: null,
                editMode: false,
                renderCalendar: true,
                removeRateId: null,
            };
        },
        inject: ['propertyId'],
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            ...mapGetters('setupPropertyStore', ['wizardData', 'initialData', 'propertyChoices']),
            dataSource() {
                this.reRenderCalendar();
                return this.ratesList.rates.map((item) => {
                    return {
                        startDate: this.generateDate(item.period.from_date),
                        endDate: this.generateDate(item.period.to_date),
                        color: item.color,
                        rateName: item.name,
                        minNights: item.min_nights,
                        nightlyRate: item.daily_rate,
                        id: item.id,
                        default: item.default,
                    };
                });
            },
            rateModalTitle() {
                if (this.values && this.values.id) {
                    return 'Edit Rate';
                }
                return 'Add Rate';
            },
            ratesTabs() {
                return [
                    {
                        tabName: 'Rates',
                        id: 'rates_rules',
                        headerTitle: 'Rates / Rules',
                        route: '',
                    },
                    {
                        tabName: 'Rates Settings',
                        id: 'rates',
                        headerTitle: 'Rate Settings',
                    },
                    {
                        tabName: 'Rules Settings',
                        id: 'rules',
                        headerTitle: 'Rule Settings',
                    },
                ];
            },
            selectedTab() {
                return this.ratesTabs.find((item) => item.id === this.currentTab);
            },
            isRatesTab() {
                return this.currentTab === 'rates_rules';
            },
            isRatesSettingsTab() {
                return this.currentTab === 'rates';
            },
            isRulesSettingsTab() {
                return this.currentTab === 'rules';
            },
            ratesCopyColumnDefs() {
                return [
                    {
                        field: '',
                        headerCheckboxSelection: true,
                        headerCheckboxSelectionFilteredOnly: true,
                        checkboxSelection: true,
                    },
                    {
                        field: 'name',
                        headerName: 'Name',
                        minWidth: 200,
                    },
                    {
                        field: 'year',
                        headerName: 'Year',
                    },
                    {
                        field: 'min_nights',
                        headerName: 'Rule',
                    },
                    {
                        field: 'daily_rate',
                        headerName: 'Nightly',
                    },
                    {
                        field: 'weekly_rate',
                        headerName: 'Weekly',
                    },
                    {
                        field: 'monthly_rate',
                        headerName: 'Monthly',
                    },
                ];
            },
            columnDefs() {
                return [
                    {
                        field: 'name',
                        headerName: 'Name',
                        minWidth: 240,
                        autoWidthExclude: true,
                        flex: 2, // set flex to 2 to make the column take up the remaining space
                    },
                    {
                        field: 'period.display',
                        headerName: 'Period',
                        minWidth: 240,
                    },
                    {
                        field: 'min_nights',
                        headerName: 'Min Nts',
                        minWidth: 120,
                        maxWidth: 120,
                    },
                    {
                        field: 'daily_rate',
                        headerName: 'Ntly',
                    },
                    {
                        field: 'weekend_rate',
                        headerName: 'Wknd',
                        cellRenderer: ({ data }) => (data && data.weekend_rate) || 'N/A',
                    },
                    {
                        field: 'weekly_rate',
                        headerName: 'Weekly',
                        hide: this.ratesData && !this.ratesData.rate_settings.use_weekly_rates,
                        cellRenderer: ({ data }) => (data && data.weekly_rate) || this.getWeeklyRatesPlaceholder(false),
                    },
                    {
                        field: 'monthly_rate',
                        headerName: 'Monthly',
                        hide: this.ratesData && !this.ratesData.rate_settings.use_monthly_rates,
                        cellRenderer: ({ data }) =>
                            (data && data.monthly_rate) || this.getMonthlyRatesPlaceholder(false),
                    },
                    {
                        field: 'arrival_day_display',
                        headerName: 'Arrive Day	',
                        minWidth: 130,
                        hide: this.ratesData && !this.ratesData.rule_settings.arrival_day_rule_enabled,
                    },
                    {
                        field: 'departure_day_display',
                        headerName: 'Depart Day',
                        minWidth: 130,

                        hide: this.ratesData && !this.ratesData.rule_settings.departure_day_rule_enabled,
                    },
                    {
                        field: 'extra_person_base',
                        minWidth: 120,
                        maxWidth: 120,
                        headerName: 'Base Guests',
                        cellRenderer: ({ data }) => (data && data.extra_person_base) || 'N/A',
                        hide: this.ratesData && !this.ratesData.rate_settings.use_per_person_rates,
                    },
                    {
                        field: 'extra_person_rate',
                        headerName: 'Addtl Guest Rate',
                        minWidth: 130,
                        cellRenderer: ({ data }) => (data && data.extra_person_rate) || 'N/A',
                        hide: this.ratesData && !this.ratesData.rate_settings.use_per_person_rates,
                    },
                    {
                        field: '',
                        cellRenderer: 'actionCellRenderer',
                        hide: this.ratesData && this.ratesData.read_only,
                        minWidth: 170,
                        maxWidth: 170,
                        cellRendererParams: {
                            currentYear: this.ratesData && this.ratesData.current_year,
                            rates: this.ratesList.rates,
                            rateSettings: this.ratesData.rate_settings,
                            ruleSettings: this.ratesData.rule_settings,
                            maxGuests: this.ratesData.max_guests,
                            removeRate: async (id) => this.removeRate(id),
                            createEditRate: async (editMode, values) => {
                                return await this.createEditRate(values);
                            },
                        },
                    },
                ];
            },
        },
        mounted() {
            this.fetchAllData();
        },
        methods: {
            getRate,
            getIsActive(id) {
                return this.currentTab === id;
            },
            onReset() {
                const period = clone(this.values.period);
                const name = this.values.name;
                this.values = JSON.parse(JSON.stringify(newRateObject));
                this.values.name = name;
                this.values.period = period;
            },
            onRemoveRateFromModal(id) {
                this.removeRateId = id;
                this.$bvModal.hide('create-rate-modal');
                this.$nextTick(() => this.$bvModal.show('remove-rate-modal'));
            },
            onDayClick(value) {
                const rate = this.getRate(this.ratesList.rates, value.date);
                if (rate) {
                    this.values = JSON.parse(JSON.stringify(rate));
                    this.editMode = true;
                    this.$nextTick(() => this.$bvModal.show('create-rate-modal'));
                }
            },
            onSelectRange(value) {
                if (value.endDate != value.startDate) {
                    const name = `${format(value.startDate, this.userUnicodeDateFormat)} to ${format(
                        value.endDate,
                        this.userUnicodeDateFormat
                    )}`;
                    this.values = {
                        ...JSON.parse(JSON.stringify(newRateObject)),
                        name,
                        period: {
                            from_date: format(value.startDate, 'yyyy-MM-dd'),
                            to_date: format(value.endDate, 'yyyy-MM-dd'),
                        },
                    };
                    this.editMode = false;
                    this.$nextTick(() => this.$bvModal.show('create-rate-modal'));
                }
            },
            async removeRate(id) {
                try {
                    this.$parent.$emit('busy', true);
                    await api.post(RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.propertyId, id).deleteRate);
                    await this.fetchRatesList(this.ratesData.current_year);
                    this.$bvModal.hide('remove-rate' + id);
                    this.$bvModal.hide('create-rate-modal');
                    this.successToast({
                        text: 'Rate successfuly deleted.',
                    });
                    this.reRenderCalendar();
                    this.removeRateId = null;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not delete rate.',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            clearCopyRates() {
                this.ratesForCopy = [];
                this.selectedSource = null;
                this.currentSelectedRows = [];
            },
            generateDate(dateString) {
                if (dateString) {
                    const datesArr = dateString.split('-');
                    return new Date(datesArr[0], datesArr[1] - 1, datesArr[2]);
                }
                return null;
            },
            onRowUpdated({ api }) {
                api.selectAll();
                this.$nextTick(() => {
                    this.currentSelectedRows = api.getSelectedRows();
                });
            },
            onChangeYear(value) {
                if (value !== this.currentYear) {
                    this.currentYear = value;
                    this.fetchRatesList(value);
                }
            },
            async createEditRate(values) {
                try {
                    this.loading = true;
                    await api.post(RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.propertyId, values.id).saveRate, {
                        ...values,
                        from_date: values.period.from_date,
                        to_date: values.period.to_date,
                        period: {
                            from_date: values.period.from_date,
                            to_date: values.period.to_date,
                        },
                    });
                    await this.fetchRatesList(this.ratesData.current_year);
                    this.reRenderCalendar();
                    this.$bvModal.hide('create-rate-modal');
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || `Could not update rate`,
                    });
                } finally {
                    this.loading = false;
                }
            },
            reRenderCalendar() {
                this.renderCalendar = false;
                this.$nextTick(() => {
                    this.renderCalendar = true;
                });
            },
            async copyRates() {
                try {
                    this.loading = true;
                    const selectedRates = this.ratesForCopy.map((item) => ({
                        ...item,
                        selected: !!this.currentSelectedRows.find((row) => row.id === item.id),
                    }));
                    await api.post(RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.propertyId).copyRates, {
                        rateChoices: selectedRates,
                        rental_property: {
                            id: this.selectedSource,
                            name: this.propertyChoices.find((item) => item.value === this.selectedSource)?.text,
                        },
                    });
                    await this.fetchRatesList(this.ratesData.current_year);

                    this.selectedSource = null;
                    this.reRenderCalendar();
                    this.$bvModal.hide(this.copyRatesModalId);
                    this.successToast({ text: 'Rates successfully copied.' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not copy rates' });
                } finally {
                    this.loading = false;
                }
            },
            async fetchAllData() {
                await this.fetchRatesData();
                if (this.ratesData) {
                    await this.fetchRatesList(this.ratesData.current_year);
                }
            },
            async fetchRatesData() {
                try {
                    this.$parent.$emit('busy', true);
                    const { data } = await api.get(RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.propertyId).getData);
                    this.ratesData = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not fetch rates data',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
            async fetchRatesForCopy() {
                if (this.selectedSource) {
                    try {
                        this.loading = true;
                        const { data } = await api.get(
                            RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.selectedSource).getAllRates
                        );
                        this.ratesForCopy = data.rates;
                    } catch (e) {
                        const { generic } = formatResponseError(e);
                        this.errorToast({
                            text: generic || 'Could not fetch rates list',
                        });
                    } finally {
                        this.loading = false;
                    }
                }
            },
            async cloneRates() {
                try {
                    this.loading = true;
                    await api.post(
                        RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.propertyId, this.ratesData.current_year)
                            .clonePreviousYearRates
                    );
                    await this.fetchRatesList(this.ratesData.current_year);
                    this.reRenderCalendar();
                    this.$bvModal.hide(this.clonePreviousYearRatesModalId);
                    this.successToast({ text: 'Rates successfully cloned.' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not clone rates',
                    });
                } finally {
                    this.loading = false;
                }
            },
            handleSelectionChange() {
                const gridApi = this.$refs.grid_copy_rates.gridOptions.api;
                this.currentSelectedRows = gridApi.getSelectedRows();
            },
            async fetchRatesList(currentYear) {
                try {
                    this.ratesData.current_year = currentYear;
                    this.$parent.$emit('busy', true);
                    const { data } = await api.get(
                        RENTAL_PROPERTY_SETUP_RATES_ENDPOINT(this.propertyId, currentYear).getRates
                    );
                    this.ratesList = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({
                        text: generic || 'Could not fetch rates list',
                    });
                } finally {
                    this.$parent.$emit('busy', false);
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .rates-page {
        padding-right: 15px;
    }

    .rates-list-grid {
        height: 250px !important;
    }

    .settings-tab-wrapper {
        a {
            width: 140px;
            margin-right: 0.5rem;
        }
        @include media-breakpoint-down(md) {
            a {
                width: 100%;
                margin-right: 0;
                margin-bottom: 0.5rem;
            }
        }
        a.active {
            background-color: transparent !important;
            border-color: #ccc !important;
            box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.3) !important;
        }
    }
    :deep(.rate-question-card) {
        margin-bottom: 10px !important;
        border: 1px solid #0d47a1;
        border-radius: 4px;

        .card-header {
            background-color: #0d47a1 !important;
            color: #fff;
            padding: 10px !important;
            font-weight: bold;
            align-items: center;
            justify-content: left !important;
            min-height: 50px;
            padding-left: 15px !important;
        }

        .card-body {
            padding: 10px !important;
        }
    }
</style>
